import {
  EMPTY_PANEL_DATA,
  PANELS,
  type Ctx,
  type MissingHint,
  type PanelDef,
  type PanelGroup,
  type PanelKey,
} from './panel-registry';
import type { Vendor } from './panel-rules';

/**
 * Engine kelengkapan: registry + data → status tiap panel + kesimpulan global.
 *
 * Menggantikan `switch` raksasa. Dipanggil dari `computed()` di store — JANGAN dipanggil dari
 * ekspresi template (akan jalan ratusan kali per detik).
 *
 * Lihat plans/KONSEP-PANEL-VALIDATION.md §7–§8 dan plans/vendors/profile/PLAN-PROFILE-TAB.md §10.
 */

/**
 * Tiga keadaan baris (PLAN-PROFILE-TAB §D7) — dihitung DI SINI supaya template tinggal `@switch`,
 * bukan `@if` bertingkat.
 */
export type PanelState = 'VALID' | 'INVALID' | 'UNAVAILABLE';

export interface PanelStatus {
  key: PanelKey;
  labelKey: string;
  messageKey: string | null;
  route: string | null;
  group: PanelGroup;
  required: boolean;
  valid: boolean;
  state: PanelState;
  missing: readonly MissingHint[];
}

export interface EvaluateResult {
  panels: readonly PanelStatus[];
  allComplete: boolean;
}

export function evaluate(ctx: Ctx): EvaluateResult {
  // Beda dari konsep: panel non-wajib TIDAK dibuang dari daftar — ia tetap tampil (tanpa badge
  // status) sesuai keputusan KONSEP §10. Yang memfilter adalah `allComplete` & `requiredPanels`.
  const panels = PANELS.map((p): PanelStatus => {
    const required = p.required(ctx);
    const valid = required ? p.validate(ctx) : false;

    return {
      key: p.key,
      labelKey: p.labelKey,
      messageKey: p.messageKey,
      route: p.route,
      group: p.group,
      required,
      valid,
      state: !required ? 'UNAVAILABLE' : valid ? 'VALID' : 'INVALID',
      missing: !required || valid || !p.missing ? [] : p.missing(ctx),
    };
  });

  return {
    panels,
    allComplete: panels.filter((p) => p.required).every((p) => p.valid),
  };
}

/**
 * Panel mana yang perlu di-fetch. Hanya membaca `vendor` — `required()` dipanggil sebelum data
 * ada, jadi `EMPTY_PANEL_DATA` yang diberikan sebagai `data`.
 */
export function requiredPanels(vendor: Vendor): PanelDef[] {
  const ctx: Ctx = { vendor, data: EMPTY_PANEL_DATA };
  return PANELS.filter((p) => p.required(ctx));
}
